import type { MouseEvent } from 'react';
import { cn } from '@/lib/utils';
import { icons } from 'lucide-react';
import { Link, useLocation } from 'react-router';
import { useSidebar } from '@/components/organisms';

export interface SidebarLinkProps {
  icon: keyof typeof icons;
  text: string;
  pathname: string;
  tourId?: string;
}

function SidebarLink({
  icon,
  text,
  pathname,
  tourId,
  onBeforeNavigate,
}: SidebarLinkProps & {
  onBeforeNavigate?: (pathname: string) => boolean;
}) {
  const location = useLocation();
  const { setOpenMobile } = useSidebar();

  const Icon = icons[icon];
  const isActive = location.pathname === pathname;

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (onBeforeNavigate && !onBeforeNavigate(pathname)) {
      event.preventDefault();
      return;
    }
    setOpenMobile(false);
  };

  return (
    <Link
      to={pathname}
      onClick={handleClick}
      data-tour={tourId}
      className={cn(
        'flex items-center gap-3 rounded-sm px-3 py-2 w-full max-w-64',
        'hover:bg-accent/50',
        'transition-colors duration-200',
        { 'bg-primary text-primary-foreground pointer-events-none': isActive }
      )}
    >
      <Icon className="size-5" />
      <span className="text-sm font-medium">{text}</span>
    </Link>
  );
}

export { SidebarLink };
